import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import companyService from '../services/companyService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

/**
 * Company profile and white-label branding.
 *
 * These values control the company identity and colours shown in the portal.
 */
const styles = {
  page: { maxWidth: 860, margin: '0 auto' },
  title: { margin: 0, fontSize: 22, fontWeight: 650, color: '#111827', letterSpacing: '-0.01em' },
  sub: { margin: '4px 0 20px', fontSize: 13, color: '#6b7280' },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 22,
    marginBottom: 18,
  },
  sectionTitle: { margin: '0 0 4px', fontSize: 15, fontWeight: 650, color: '#111827' },
  sectionHint: { margin: '0 0 16px', fontSize: 12, color: '#6b7280', lineHeight: 1.55 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151' },
  input: {
    padding: '9px 11px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 13.5,
    fontFamily: 'inherit',
    color: '#111827',
    background: '#fff',
  },
  primaryBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #fff)',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  preview: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 10,
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    marginBottom: 18,
  },
  swatch: { width: 34, height: 34, borderRadius: 8, flexShrink: 0 },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 14,
  },
  ok: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#166534',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 14,
  },
};

const EMPTY = {
  name: '',
  logoUrl: '',
  primaryColor: '',
};

function normalizeHexColor(value) {
  const color = value.trim();
  if (/^#[\da-f]{6}$/i.test(color)) return color.toLowerCase();
  const shorthand = color.match(/^#([\da-f])([\da-f])([\da-f])$/i);
  return shorthand ? `#${shorthand[1]}${shorthand[1]}${shorthand[2]}${shorthand[2]}${shorthand[3]}${shorthand[3]}`.toLowerCase() : '';
}

function getContrastColor(hex) {
  const color = normalizeHexColor(hex) || '#273947';
  const channels = [1, 3, 5].map((offset) => parseInt(color.slice(offset, offset + 2), 16));
  return (channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114) > 150 ? '#111827' : '#ffffff';
}

export default function CompanySettingsPage() {
  const { user } = useAuth();
  const { refreshBranding } = useBranding();
  const [searchParams] = useSearchParams();

  const isPlatformOwner = user?.role === 'superadmin';
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(searchParams.get('company') || user?.clinicId || '');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isPlatformOwner) return;
    companyService.getAll()
      .then((list) => {
        setCompanies(list || []);
        const selectedCompanyId = searchParams.get('company') || user?.clinicId || list?.[0]?.id || '';
        setCompanyId((current) => current || selectedCompanyId);
        if (selectedCompanyId) refreshBranding(selectedCompanyId);
      })
      .catch(() => { /* the picker just stays empty */ });
  }, [isPlatformOwner, refreshBranding, searchParams, user?.clinicId]);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    companyService.getById(companyId)
      .then((c) => {
        if (cancelled || !c) return;
        setForm({
          name: c.name || '',
          logoUrl: c.logoUrl || '',
          primaryColor: c.primaryColor || c.color || '',
        });
      })
      .catch((err) => setError(err?.response?.data?.error || 'Could not load the company profile.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId]);

  const setField = (key) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);

    if (!form.name.trim()) return setError('Company name is required.');
    if (form.primaryColor.trim() && !normalizeHexColor(form.primaryColor)) {
      return setError('Enter a valid colour in #RRGGBB format, or choose it with the colour picker.');
    }

    setSaving(true);
    try {
      await companyService.updateBranding(companyId, {
        name: form.name.trim(),
        primaryColor: form.primaryColor.trim() ? normalizeHexColor(form.primaryColor) : '',
        locale: 'en',
      });
      refreshBranding(companyId);
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save the company profile.');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setError('');
    setSaved(false);

    if (file.size > 5 * 1024 * 1024) return setError('Logo images must be 5 MB or smaller.');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      return setError('Choose a PNG, JPEG, or WebP image.');
    }

    setUploadingLogo(true);
    try {
      const result = await companyService.uploadLogo(companyId, file);
      setForm((current) => ({ ...current, logoUrl: result.logoUrl }));
      refreshBranding(companyId);
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not upload the company logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const primaryColor = normalizeHexColor(form.primaryColor) || '#273947';

  if (!companyId) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Company profile</h1>
        <p style={styles.sub}>
          Your account is not linked to a company. Ask the platform owner to assign one.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Company profile</h1>
      <p style={styles.sub}>
        Name, logo and colours shown to the people who receive your forms. Blank fields fall back
        to the platform defaults.
      </p>

      {isPlatformOwner && companies.length > 0 && (
        <div style={{ ...styles.card, padding: 16, marginBottom: 18 }}>
          <div style={styles.field}>
            <label style={styles.label}>Editing company</label>
            <select
              style={styles.input}
              value={companyId}
              onChange={(e) => { setCompanyId(e.target.value); refreshBranding(e.target.value); setSaved(false); }}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.isActive === false ? ' (suspended)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}
      {saved && <div style={styles.ok}>Company profile saved.</div>}

      {loading ? (
        <div style={styles.card}>Loading…</div>
      ) : (
        <form onSubmit={submit}>
          {/* Live preview of the values recipients will see */}
          <div style={styles.preview}>
            <div style={{ ...styles.swatch, background: primaryColor }} />
            {form.logoUrl
              ? <img src={form.logoUrl} alt="" style={{ height: 32, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              : null}
            <div>
              <div style={{ fontWeight: 650, fontSize: 14, color: '#111827' }}>
                {form.name || 'Company name'}
              </div>
              <div style={{ marginTop: 5, display: 'inline-block', padding: '4px 9px', borderRadius: 6, background: primaryColor, color: getContrastColor(primaryColor), fontSize: 11, fontWeight: 600 }}>
                Primary colour preview
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Identity</h2>
            <p style={styles.sectionHint}>Used for the company identity and colour accents shown in the portal.</p>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Company name *</label>
                <input style={styles.input} value={form.name} onChange={setField('name')} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Company logo</label>
                <input
                  style={styles.input}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={uploadLogo}
                  disabled={uploadingLogo}
                />
                <span style={{ ...styles.sectionHint, margin: 0 }}>
                  PNG, JPEG, or WebP; maximum 5 MB. {uploadingLogo ? 'Uploading…' : 'The logo is saved when the upload finishes.'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Primary colour</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="color"
                    style={{ ...styles.input, padding: 4, width: 52, height: 38 }}
                    value={primaryColor}
                    onChange={(e) => {
                      setSaved(false);
                      setForm((current) => ({ ...current, primaryColor: e.target.value }));
                    }}
                  />
                  <input
                    style={{ ...styles.input, flex: 1, minWidth: 0, borderColor: form.primaryColor.trim() && !normalizeHexColor(form.primaryColor) ? '#dc3545' : styles.input.border }}
                    value={form.primaryColor}
                    onChange={setField('primaryColor')}
                    placeholder="#273947"
                    aria-invalid={Boolean(form.primaryColor.trim() && !normalizeHexColor(form.primaryColor))}
                  />
                </div>
                <span style={{ ...styles.sectionHint, margin: 0 }}>
                  {form.primaryColor.trim() && !normalizeHexColor(form.primaryColor)
                    ? 'Enter #RRGGBB with 6 hexadecimal digits, or pick a colour.'
                    : 'The colour picker, hex value, and preview stay in sync.'}
                </span>
              </div>

            </div>
          </div>

          <button type="submit" style={styles.primaryBtn} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      )}
    </div>
  );
}
